import { getBitValueFromBool, getBoolFromBitValue } from '../../common/utils/helpers';
import { ACCEPT_ALL_PREFERENCES, ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY, DEFAULT_PREFERENCES, ONLY_NECESSARY_PREFERENCES, V002_SCHEMA_KEY } from '../../constants';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { getCookie } from '../../controllers/cookie-controls/get-cookie';
import { ConsentPreference, CookieCategory } from '../../types';
import { COUNTRIES_PROTECTED_BY_FADP, COUNTRIES_PROTECTED_BY_GDPR, COUNTRIES_PROTECTED_BY_LGPD, TIER_1_PROTECTED_LOCALES, TIER_2_PROTECTED_LOCALES, TIER_3_UNPROTECTED_LOCALES, US_STATES_PROTECTED_BY_CPRA } from './constants';
import { ConsentHubKey } from './types';
export const isLocaleCoveredByTier1Policies = localeToCheck => {
  // Locale formatted as <CountryCode>-<SubdivisionCode>
  const localeCountryCode = localeToCheck.split('-')[0];
  return TIER_1_PROTECTED_LOCALES.includes(localeCountryCode);
};
export const isLocaleCoveredByTier2Policies = localeToCheck => {
  // As Tier 2 is only US states currently, grabbing the country code here would be irrelevant
  return TIER_2_PROTECTED_LOCALES.includes(localeToCheck);
};
export const isLocaleCoveredByGDPR = localeToCheck => {
  const localeCountryCode = localeToCheck.split('-')[0];
  return COUNTRIES_PROTECTED_BY_GDPR.includes(localeCountryCode);
};
export const isLocaleCoveredByLGPD = localeToCheck => {
  const localeCountryCode = localeToCheck.split('-')[0];
  return COUNTRIES_PROTECTED_BY_LGPD.includes(localeCountryCode);
};
export const isLocaleCoveredByFADP = localeToCheck => {
  const localeCountryCode = localeToCheck.split('-')[0];
  return COUNTRIES_PROTECTED_BY_FADP.includes(localeCountryCode);
};
export const isLocaleCoveredByCPRA = localeToCheck => {
  return US_STATES_PROTECTED_BY_CPRA.includes(localeToCheck);
};
export const getConsentedUnderPrivacyActPrefs = localeToCheck => {
  const isTier2Locale = isLocaleCoveredByTier2Policies(localeToCheck);
  const coveredByGDPR = isLocaleCoveredByGDPR(localeToCheck);
  const coveredByCPRA = isLocaleCoveredByCPRA(localeToCheck);
  const coveredByLGPD = isLocaleCoveredByLGPD(localeToCheck);
  const coveredByFADP = isLocaleCoveredByFADP(localeToCheck);

  // To maintain token granularity, if a locale is covered by Tier 1 policies but not by LGPD or FADP, we'll treat it as GDPR
  const isTier1LocaleWithoutRegionalLaws = isLocaleCoveredByTier1Policies(localeToCheck) && !coveredByLGPD && !coveredByFADP;
  return {
    [ConsentPreference.ConsentedUnderGDPR]: coveredByGDPR || isTier1LocaleWithoutRegionalLaws,
    [ConsentPreference.ConsentedUnderCPRA]: coveredByCPRA || isTier2Locale,
    [ConsentPreference.ConsentedUnderLGPD]: coveredByLGPD,
    [ConsentPreference.ConsentedUnderFADP]: coveredByFADP
  };
};
export const isLocaleCoveredByAnyPrivacyActs = localeToCheck => {
  // 'UNKNOWN' means locale can't be determined by GeoIP/service, so be extra compliant and Fail Closed/block by default
  return isLocaleCoveredByTier1Policies(localeToCheck) || isLocaleCoveredByTier2Policies(localeToCheck) || localeToCheck === 'UNKNOWN';
};
export const checkIfTokenIsDefault = consentToken => {
  const consentsUnavailable = getBoolFromBitValue(consentToken[7]);
  const defaultPrefsWithProtections = getDefaultBits(TIER_1_PROTECTED_LOCALES[0], consentsUnavailable);
  const defaultPrefsWithoutProtections = getDefaultBits(TIER_3_UNPROTECTED_LOCALES[0], consentsUnavailable);
  return consentToken === defaultPrefsWithProtections || consentToken === defaultPrefsWithoutProtections;
};
export const getDefaultPrefs = currentLocale => {
  // User is in a locale that's covered by regional protection laws (e.g. US-CA) — Fail Closed by default until we have consents
  // User is in a locale that doesn't care (e.g. US-NY) — Fail Open by default as we'll never get consents
  const localeIsCoveredByRegionalProtectionLaws = isLocaleCoveredByAnyPrivacyActs(currentLocale);
  return {
    ...DEFAULT_PREFERENCES,
    ...(localeIsCoveredByRegionalProtectionLaws ? ONLY_NECESSARY_PREFERENCES : ACCEPT_ALL_PREFERENCES)
  };
};
export const getDefaultBits = (currentLocale, consentsUnavailable) => {
  const version = V002_SCHEMA_KEY;
  const defaultPrefs = getDefaultPrefs(currentLocale);
  const serializedBits = Object.entries(defaultPrefs).reduce((acc, [key, val]) => {
    // Skip the STRICTLY_NECESSARY as it's always allowed
    if (key === CookieCategory.StrictlyNecessary) {
      return acc;
    }
    if (key === ConsentPreference.ConsentDataUnavailable) {
      // If consents are unavailable (unauthenticated), pack that data into the consent token bits
      return acc + getBitValueFromBool(!!consentsUnavailable);
    }
    return acc + getBitValueFromBool(val);
  }, '');
  return `${version}${serializedBits}`;
};
export const getSavedConsentBits = (consents, localeWhenConsented) => {
  var _consents$find, _consents$find2, _consents$find3, _consents$find4;
  const version = V002_SCHEMA_KEY;
  // As 'strictlyNecessaryConsent' would always be allowed, we don't store it in the cookie or use ConsentHub
  const functionalConsent = getBitValueFromBool(!!((_consents$find = consents.find(key => key.key === ConsentHubKey.Functional)) !== null && _consents$find !== void 0 && _consents$find.granted));
  const analyticsConsent = getBitValueFromBool(!!((_consents$find2 = consents.find(key => key.key === ConsentHubKey.Analytics)) !== null && _consents$find2 !== void 0 && _consents$find2.granted));
  const marketingConsent = getBitValueFromBool(!!((_consents$find3 = consents.find(key => key.key === ConsentHubKey.Marketing)) !== null && _consents$find3 !== void 0 && _consents$find3.granted));
  const unknownConsent = getBitValueFromBool(!!((_consents$find4 = consents.find(key => key.key === ConsentHubKey.Unknown)) !== null && _consents$find4 !== void 0 && _consents$find4.granted));
  const consentHubDataUnavailable = 0; // If we got here, we've hit ConsentHub for data, thus available
  const previouslyConsentedUnderGDPR = isLocaleCoveredByGDPR(localeWhenConsented);
  const previouslyConsentedUnderCPRA = isLocaleCoveredByCPRA(localeWhenConsented);
  const previouslyConsentedUnderLGPD = isLocaleCoveredByLGPD(localeWhenConsented);
  const previouslyConsentedUnderFADP = isLocaleCoveredByFADP(localeWhenConsented);

  // We have not yet confirmed if we want to treat individual countries with their own protection laws
  // differently from GDPR, but don't want to lose the granularity of the consentToken if we choose to do so in the future
  // so Atlassian will serve GDPR consents to all Tier 1 locales until then, but keep the individual country consents if present
  const consentedUnderTier1WithoutRegionalLaws = isLocaleCoveredByTier1Policies(localeWhenConsented) && !previouslyConsentedUnderLGPD && !previouslyConsentedUnderFADP;

  // Similarly, we don't want to lose the granularity of the consentToken if we choose to treat Tier 2 locales differently
  // in the future, so Atlassian will serve CPRA consents to all Tier 2 locales until then.
  const consentedUnderTier2WithoutRegionalLaws = isLocaleCoveredByTier2Policies(localeWhenConsented) && !previouslyConsentedUnderCPRA;
  const consentedUnderGDPR = getBitValueFromBool(previouslyConsentedUnderGDPR || consentedUnderTier1WithoutRegionalLaws);
  const consentedUnderCPRA = getBitValueFromBool(previouslyConsentedUnderCPRA || consentedUnderTier2WithoutRegionalLaws);
  const consentedUnderLGPD = getBitValueFromBool(previouslyConsentedUnderLGPD);
  const consentedUnderFADP = getBitValueFromBool(previouslyConsentedUnderFADP);
  return [version, functionalConsent, analyticsConsent, marketingConsent, unknownConsent, consentHubDataUnavailable, consentedUnderGDPR, consentedUnderCPRA, consentedUnderLGPD, consentedUnderFADP].join('');
};
export const serializeConsents = ({
  consents,
  localeWhenConsented,
  currentLocale
}) => {
  if (!consents) {
    // When generating consents for an unauthenticated user, use the fallback token if it exists
    // This circumvents us setting defaults for them if their token has expired, but they have consented previously
    const existingFallbackToken = getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY);
    if (existingFallbackToken) {
      return existingFallbackToken;
    }
    // User is unauthed, will only use local consenting, thus consent data is unavailable
    return getDefaultBits(currentLocale, true); // e.g. 002000010000 | 002111110000
  } else if (consents.length === 0) {
    // User is authed but has no consents in the system, so use defaults in the interim, but consentData WILL be available
    return getDefaultBits(currentLocale); // e.g. 002000000000 | 002111100000
  } else {
    // User is authed and has consents in the system, which means they also have localeWhenConsented
    return getSavedConsentBits(consents, localeWhenConsented);
  }
};
export const deserializeConsents = (unpackedUserPreferences, displayedTextMap) => {
  const validConsentHubCookieConsents = [CookieCategory.StrictlyNecessary, CookieCategory.Functional, CookieCategory.Analytics, CookieCategory.Marketing, CookieCategory.Unknown];
  const consentKeyMap = {
    [CookieCategory.StrictlyNecessary]: ConsentHubKey.StrictlyNecessary,
    [CookieCategory.Functional]: ConsentHubKey.Functional,
    [CookieCategory.Analytics]: ConsentHubKey.Analytics,
    [CookieCategory.Marketing]: ConsentHubKey.Marketing,
    [CookieCategory.Unknown]: ConsentHubKey.Unknown
  };
  const consentRecords = validConsentHubCookieConsents.map(consentKey => {
    const key = consentKeyMap[consentKey];
    return {
      key,
      granted: !!unpackedUserPreferences[consentKey],
      gatheredTs: new Date().toISOString(),
      displayedText: displayedTextMap[consentKey]
    };
  });
  return consentRecords;
};
export const getConsentTimestamp = ({
  consents
}) => {
  if (consents && consents.length) {
    // As consents can be updated individually, we need to find the most recent timestamp to use as "most recent consent"

    const timestamps = consents.map(consent => {
      return new Date(consent.updatedTs).getTime();
    });
    const maxTimestamp = Math.max(...timestamps);
    const mostRecentConsentTimestamp = new Date(maxTimestamp).toISOString();
    return mostRecentConsentTimestamp;
  }
};
export const checkIfConsentsAreStale = consentTimestamp => {
  // Privacy has determined that we should re-prompt users for consent if they haven't
  // consented in over a year regardless, because they're legally stale/expired
  let consentedOverAYearAgo = false;
  if (consentTimestamp) {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    consentedOverAYearAgo = new Date(consentTimestamp) < oneYearAgo;
  }
  return consentedOverAYearAgo;
};
export const checkIfTokenHasAConsentLocale = consentToken => {
  const consentedUnderGDPR = getBoolFromBitValue(consentToken[8]);
  const consentedUnderCPRA = getBoolFromBitValue(consentToken[9]);
  const consentedUnderLGPD = getBoolFromBitValue(consentToken[10]);
  const consentedUnderFADP = getBoolFromBitValue(consentToken[11]);
  return !!(consentedUnderGDPR || consentedUnderCPRA || consentedUnderLGPD || consentedUnderFADP);
};
export class StatusCodeError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
    Object.setPrototypeOf(this, StatusCodeError.prototype);

    // Captures the stack trace (for environments that support it)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, StatusCodeError);
    }
    this.name = 'StatusCodeError';
  }
}